import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { Reference } from '@apollo/client';
import { ModalCloseButton } from '@coverright/ui/shared';
import { FormikTextInput, SelectInput } from '@coverright/ui/inputs';
import { enqueueSnackbar } from 'notistack';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { useConfirm } from '@coverright/features';
import {
  CalendarEventFieldsFragmentDoc,
  CalendarEventOutput,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
  useCreateCalendarEventMutation,
  useDeleteCalendarEventMutation,
  useUpdateCalendarEventMutation,
} from '../calendar-types';
import { AttendeeSelect } from './attendee-select';
import { CalendarDatetimeRange } from './calendar-datetime-range';
import { CalendarTimezoneSelect } from './calendar-timezone-select';
import { isCalendarAuthError } from './calendar-utils';
import { CALENDAR_COLORS, DEFAULT_COLOR } from './calendar-colors';

const OPTIMISTIC_NEW_EVENT_ID = 'optimistic-new-event';
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface EventFormModalProps {
  open: boolean;
  onClose: () => void;
  agentEmail?: string | null;
  event?: CalendarEventOutput | null;
  defaultStart?: string | null;
  onMutationComplete?: (eventId: string | undefined) => void;
  onDeleteComplete?: () => void;
}


const toDatetimeLocal = (iso?: string | null, tz?: string | null) =>
  iso ? (tz ? moment.tz(iso, tz) : moment(iso)).format('YYYY-MM-DDTHH:mm') : '';

const toIso = (datetimeLocal: string, tz: string) =>
  moment.tz(datetimeLocal, tz).toISOString();

interface FormValues {
  title: string;
  startDateTime: string;
  endDateTime: string;
  timeZone: string;
  description: string;
  location: string;
  attendeeEmails: string[];
  sendNotifications: boolean;
  colorId: string;
  reminderMinutes: string;
  addMeetLink: boolean;
}

const validationSchema = Yup.object({
  title: Yup.string().trim().required('Title is required'),
  startDateTime: Yup.string().required('Start date/time is required'),
  endDateTime: Yup.string()
    .required('End date/time is required')
    .test('after-start', 'End must be after start', function (val) {
      const { startDateTime } = this.parent;
      return !startDateTime || !val || moment(val).isAfter(moment(startDateTime));
    }),
  timeZone: Yup.string().required('Timezone is required'),
  attendeeEmails: Yup.array().of(
    Yup.string().email('Invalid email address')
  ),
  reminderMinutes: Yup.number()
    .nullable()
    .transform((v, o) => (o === '' ? null : v))
    .min(0, 'Reminder must be a positive number'),
});

export const EventFormModal = ({ open, onClose, agentEmail, event, defaultStart, onMutationComplete, onDeleteComplete }: EventFormModalProps) => {
  const isEdit = !!event;
  const theme = useTheme();
  const confirm = useConfirm(theme);

  const [create, { loading: creating }] = useCreateCalendarEventMutation();
  const [update, { loading: updating }] = useUpdateCalendarEventMutation();
  const [del, { loading: deleting }] = useDeleteCalendarEventMutation();

  const loading = creating || updating || deleting;
  const titleRef = React.useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    formik.setTouched(Object.keys(errors).reduce<Record<string, boolean>>((acc, k) => ({ ...acc, [k]: true }), {}));
    if (errors.title) {
      titleRef.current?.focus();
      return;
    }
    formik.submitForm();
  };

  const buildInitialValues = (): FormValues => {
    const eventTz = event?.timeZone ?? null;
    const defaultStartVal = event?.startDateTime
      ? toDatetimeLocal(event.startDateTime, eventTz)
      : defaultStart
      ? toDatetimeLocal(defaultStart)
      : toDatetimeLocal(moment().startOf('hour').add(1, 'hour').toISOString());

    const defaultEndVal = event?.endDateTime
      ? toDatetimeLocal(event.endDateTime, eventTz)
      : defaultStart
      ? toDatetimeLocal(moment(defaultStart).add(1, 'hour').toISOString())
      : toDatetimeLocal(moment().startOf('hour').add(2, 'hour').toISOString());

    return {
      title: event?.summary || '',
      startDateTime: defaultStartVal,
      endDateTime: defaultEndVal,
      timeZone: event?.timeZone || BROWSER_TIMEZONE,
      description: event?.description || '',
      location: event?.location || '',
      attendeeEmails: event?.attendees?.map((a) => a.email).filter((e): e is string => !!e) || [],
      sendNotifications: true,
      colorId: event?.colorId || '',
      reminderMinutes: event?.reminderMinutes != null ? String(event.reminderMinutes) : '10',
      addMeetLink: isEdit ? !!event?.meetLink : true,
    };
  };

  const formik = useFormik<FormValues>({
    initialValues: buildInitialValues(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const reminderMinutesVal = values.reminderMinutes !== '' ? parseInt(values.reminderMinutes, 10) : undefined;
      const shared = {
        summary: values.title,
        startDateTime: toIso(values.startDateTime, values.timeZone),
        endDateTime: toIso(values.endDateTime, values.timeZone),
        timeZone: values.timeZone,
        description: values.description || undefined,
        location: values.location || undefined,
        attendeeEmails: values.attendeeEmails.length ? values.attendeeEmails : undefined,
        sendNotifications: values.sendNotifications,
        colorId: values.colorId || undefined,
        reminderMinutes: reminderMinutesVal,
      };

      const buildOptimisticEvent = (id: string | null): CalendarEventOutput => ({
        __typename: 'CalendarEventOutput',
        id,
        summary: shared.summary ?? null,
        description: shared.description ?? null,
        location: shared.location ?? null,
        startDateTime: shared.startDateTime ?? null,
        endDateTime: shared.endDateTime ?? null,
        timeZone: shared.timeZone ?? null,
        status: event?.status ?? null,
        htmlLink: event?.htmlLink ?? null,
        colorId: shared.colorId ?? null,
        reminderMinutes: reminderMinutesVal ?? null,
        etag: event?.etag ?? null,
        meetLink: event?.meetLink ?? null,
        dialInPhone: event?.dialInPhone ?? null,
        dialInPin: event?.dialInPin ?? null,
        organizerName: event?.organizerName ?? null,
        morePhoneNumbersUrl: event?.morePhoneNumbersUrl ?? null,
        attendeeEmails: shared.attendeeEmails ?? event?.attendeeEmails ?? [],
        attendees: shared.attendeeEmails
          ? shared.attendeeEmails.map((email) => ({
              __typename: 'CalendarEventAttendeeOutput' as const,
              email,
              displayName: null,
              responseStatus: null,
              self: null,
            }))
          : (event?.attendees ?? null),
      });

      try {
        if (isEdit && event) {
          await update({
            variables: {
              eventId: event.id!,
              input: { ...shared, etag: event.etag ?? undefined } as UpdateCalendarEventInput,
              agentEmail,
            },
            optimisticResponse: {
              updateCalendarEvent: buildOptimisticEvent(event.id ?? null),
            },
            update: (cache, { data }) => {
              const updated = data?.updateCalendarEvent;
              if (!updated) return;
              cache.writeFragment({
                id: cache.identify({ __typename: 'CalendarEventOutput', id: updated.id }),
                fragment: CalendarEventFieldsFragmentDoc,
                data: updated,
              });
            },
            refetchQueries: ['calendarEvents'],
          });
          onMutationComplete?.(event.id ?? undefined);
          enqueueSnackbar(`Event updated: "${values.title}"`, { variant: 'info' });
        } else {
          const result = await create({
            variables: { agentEmail, input: { ...shared, addMeetLink: values.addMeetLink ? true : undefined } as CreateCalendarEventInput },
            optimisticResponse: {
              createCalendarEvent: buildOptimisticEvent(OPTIMISTIC_NEW_EVENT_ID),
            },
            update: (cache, { data }) => {
              const created = data?.createCalendarEvent;
              if (!created) return;
              cache.modify({
                fields: {
                  calendarEvents(existing) {
                    const newRef = cache.writeFragment({
                      data: created,
                      fragment: CalendarEventFieldsFragmentDoc,
                    });
                    return {
                      ...existing,
                      events: [...(existing?.events ?? []), newRef],
                    };
                  },
                },
              });
            },
            refetchQueries: ['calendarEvents'],
          });
          onMutationComplete?.(result.data?.createCalendarEvent?.id ?? undefined);
          enqueueSnackbar(`New event: "${values.title}"`, { variant: 'info' });
        }
        onClose();
      } catch (err: unknown) {
        const e = err as { message?: string; networkError?: { statusCode?: number } };
        if (e?.message?.includes('412') || e?.networkError?.statusCode === 412) {
          enqueueSnackbar('This event was modified by someone else. Refreshing the latest version.', { variant: 'warning' });
        } else if (e?.message && isCalendarAuthError(e.message)) {
          enqueueSnackbar("Agent's Google account is not connected to the workspace.", { variant: 'error' });
        } else {
          enqueueSnackbar(e?.message || 'Failed to save event', { variant: 'error' });
        }
      }
    },
  });

  React.useEffect(() => {
    if (open) {
      formik.resetForm({ values: buildInitialValues() });
    }
  }, [open, event?.id]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete event',
      content: 'Are you sure you want to delete this event?',
      okButtonTitle: 'Delete',
      cancelButtonTitle: 'Cancel',
    });
    if (!confirmed) return;
    if (!event?.id) return;
    try {
      await del({
        variables: { eventId: event.id, agentEmail },
        optimisticResponse: { deleteCalendarEvent: true },
        update: (cache) => {
          cache.modify({
            fields: {
              calendarEvents(existing, { readField }) {
                if (!existing?.events) return existing;
                return {
                  ...existing,
                  events: existing.events.filter(
                    (ref: Reference) => readField('id', ref) !== event.id
                  ),
                };
              },
            },
          });
        },
        refetchQueries: ['calendarEvents'],
      });
      onMutationComplete?.(event.id ?? undefined);
      enqueueSnackbar('An event was removed from the calendar', { variant: 'info' });
      (onDeleteComplete ?? onClose)();
    } catch (err: unknown) {
      const e = err as { message?: string };
      const msg = e?.message && isCalendarAuthError(e.message)
        ? "Agent's Google account is not connected to the workspace."
        : e?.message || 'Failed to delete event';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle variant="h3">
        {isEdit ? 'Edit Event' : 'New Event'}
        <ModalCloseButton onClick={onClose} sx={{ position: 'absolute', top: 4, right: 8 }} />
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflowY: 'auto' }}>
        <Stack gap={2.5}>
          <FormikTextInput name="title" label="Title *" required formik={formik} hideTick inputRef={titleRef} />

          <CalendarDatetimeRange
            startValue={formik.values.startDateTime}
            endValue={formik.values.endDateTime}
            onStartChange={(v) => formik.setFieldValue('startDateTime', v)}
            onEndChange={(v) => formik.setFieldValue('endDateTime', v)}
            startError={formik.touched.startDateTime ? formik.errors.startDateTime : undefined}
            endError={formik.touched.endDateTime ? formik.errors.endDateTime : undefined}
          />

          <CalendarTimezoneSelect
            value={formik.values.timeZone}
            onChange={(tz) => formik.setFieldValue('timeZone', tz)}
          />

          <FormikTextInput name="description" label="Description" multiline rows={3} formik={formik} hideTick />

          <FormikTextInput name="location" label="Location" formik={formik} hideTick />

          <AttendeeSelect
            value={formik.values.attendeeEmails}
            onChange={(emails) => formik.setFieldValue('attendeeEmails', emails)}
          />

          <Stack direction="row" alignItems="center" gap={1}>
            <Typography className="text-input-label" sx={{ lineHeight: '19px', fontWeight: 600, minWidth: 48 }}>
              Color
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              <Tooltip title="Default">
                <Box
                  onClick={() => formik.setFieldValue('colorId', '')}
                  sx={(theme) => ({
                    width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${DEFAULT_COLOR.border} 50%, ${theme.palette.common.white} 50%)`,
                    border: '2px solid',
                    borderColor: formik.values.colorId === '' ? 'text.primary' : 'transparent',
                    boxSizing: 'border-box',
                  })}
                />
              </Tooltip>
              {CALENDAR_COLORS.map((c) => (
                <Tooltip key={c.id} title={c.name}>
                  <Box
                    onClick={() => formik.setFieldValue('colorId', c.id)}
                    sx={{
                      width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                      backgroundColor: c.border,
                      border: '2px solid',
                      borderColor: formik.values.colorId === c.id ? 'text.primary' : 'transparent',
                      boxSizing: 'border-box',
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Stack>

          <SelectInput
            label="Reminder"
            value={formik.values.reminderMinutes}
            onChange={(e) => formik.setFieldValue('reminderMinutes', e.target.value)}
            hideTick
            className="mt-0"
          >
            <MenuItem value="5">5 minutes before</MenuItem>
            <MenuItem value="10">10 minutes before</MenuItem>
            <MenuItem value="15">15 minutes before</MenuItem>
            <MenuItem value="30">30 minutes before</MenuItem>
            <MenuItem value="60">1 hour before</MenuItem>
            <MenuItem value="120">2 hours before</MenuItem>
            <MenuItem value="1440">1 day before</MenuItem>
          </SelectInput>

          {event?.meetLink ? (
            <FormControlLabel
              sx={{ gap: 1, ml: 0 }}
              control={<Switch checked size="small" disabled />}
              label={
                <Stack>
                  <Typography variant="body2">Google Meet link</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {event.meetLink}
                  </Typography>
                </Stack>
              }
            />
          ) : (
            <Tooltip title={isEdit ? 'Google Meet links can only be added when creating an event' : ''} placement="top-start">
              <FormControlLabel
                sx={{ gap: 1, ml: 0 }}
                control={
                  <Switch
                    checked={formik.values.addMeetLink}
                    onChange={(e) => formik.setFieldValue('addMeetLink', e.target.checked)}
                    size="small"
                    disabled={isEdit}
                  />
                }
                label="Add Google Meet link"
              />
            </Tooltip>
          )}

          <FormControlLabel
            sx={{ gap: 1, ml: 0 }}
            control={
              <Switch
                checked={formik.values.sendNotifications}
                onChange={(e) => formik.setFieldValue('sendNotifications', e.target.checked)}
                size="small"
              />
            }
            label="Send notifications to attendees"
          />

          <Stack direction="row" alignItems="center" justifyContent={isEdit ? 'space-between' : 'flex-end'} gap={2}>
            {isEdit && (
              <Button color="error" variant="outlined" onClick={handleDelete} disabled={loading}>
                Delete
              </Button>
            )}
            <Stack direction="row" gap={2}>
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <LoadingButton variant="contained" onClick={handleSubmit} loading={loading}>
                {isEdit ? 'Save' : 'Create'}
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
